import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { ArrowBack, SortByAlpha, Numbers } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useCountries } from '../hooks/useMarketData';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import 'flag-icons/css/flag-icons.min.css';

export const Countries: React.FC = () => {
  usePageTitle('Select Country');
  const navigate = useNavigate();
  const { continentId } = useParams<{ continentId: string }>();
  const { data, isLoading, error } = useCountries(continentId ? parseInt(continentId) : null);
  const [sortMode, setSortMode] = useState<'marketCount' | 'alphabetical'>('marketCount');

  const countries = data?.countries || [];

  // Sort countries based on selected mode
  const sortedCountries = useMemo(() => {
    const countriesCopy = [...countries];
    if (sortMode === 'marketCount') {
      // Sort by total markets in descending order
      return countriesCopy.sort((a, b) => (b.total_markets || 0) - (a.total_markets || 0));
    } else {
      // Sort alphabetically by name
      return countriesCopy.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [countries, sortMode]);

  if (isLoading) {
    return <LoadingSpinner message="Loading countries..." />;
  }

  if (error) {
    return <ErrorMessage message="Failed to load countries" />;
  }

  return (
    <Box pb={10}>
      <Box px={2} pt={2} mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={() => navigate('/continents')}>
            <ArrowBack />
          </IconButton>
          <Box flex={1}>
            <Typography variant="h5" fontWeight="bold">
              Select Country
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {countries.length} countries available
            </Typography>
          </Box>
        </Box>

        {/* Sort Toggle */}
        <Box display="flex" justifyContent="center">
          <ToggleButtonGroup
            value={sortMode}
            exclusive
            onChange={(_, newMode) => newMode && setSortMode(newMode)}
            size="small"
          >
            <ToggleButton value="marketCount">
              <Numbers sx={{ mr: 0.5, fontSize: 20 }} />
              By Markets
            </ToggleButton>
            <ToggleButton value="alphabetical">
              <SortByAlpha sx={{ mr: 0.5, fontSize: 20 }} />
              Alphabetical
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box px={2}>
        <Grid container spacing={2}>
          {sortedCountries.map((country) => (
            <Grid item xs={12} key={country.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  },
                }}
                onClick={() => navigate(`/markets/${country.id}`)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    {country.code ? (
                      <Box sx={{
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span
                          className={`fi fi-${country.code.toLowerCase()} fis`}
                          style={{
                            fontSize: '2.5rem',
                            display: 'inline-block',
                            lineHeight: 1
                          }}
                        />
                      </Box>
                    ) : country.flag_emoji ? (
                      <Typography variant="h4">{country.flag_emoji}</Typography>
                    ) : (
                      <Typography variant="h4">🏳️</Typography>
                    )}
                    <Box>
                      <Typography variant="h6">{country.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {country.total_markets || 0} markets
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};